import { TranslationService } from '../../../services/translation.service';
import {
	DatacenterDateFilter,
	FilterType,
	DatacenterFreeTextFilter,
	DatacenterNumericFilter,
	OrderNumberFilter,
	FilterApiRequest,
	DropdownFilter,
	DocumentLastConnectionDateFilter,
	DocumentDateFilter,
	DocumentMillDropdownFilter,
	DocumentOrderNumberFilter,
	GlobalSearchFilter,
	DocumentSuggestiveListFilterWithId
} from '../../../models/datacenter-filters';
import { API_SALESDOC_SERVER_URL } from '../../../../config';
import { UserService } from '../../../services/user.service';
import { User } from '../../../models/user';

const GET_DOCUMENTS_URL = `${ API_SALESDOC_SERVER_URL }/v1/documents`;
const GET_DOCUMENTS_V2_URL = `${ API_SALESDOC_SERVER_URL }/v2/documents`;
const GET_DOCUMENTLINE_V2_URL = `${ API_SALESDOC_SERVER_URL }/v2/documentLines`;

type FilterTypeFn = () => FilterType;

export class DocumentFiltersConfiguration {
	user: User;

	constructor(
		private readonly translationService: TranslationService,
		private readonly userService: UserService
	) {
		this.user = this.userService.user;
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	DOCUMENT_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'name',
				() => new DatacenterFreeTextFilter(
					'name',
					this.translationService.getTR('commercial-documents.number'),
					this.translationService.getTR('commercial-documents.number'),
					'name'
				)
			],
			[
				'date',
				() => new DocumentDateFilter(
					'date',
					this.translationService.getTR('commercial-documents.date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'date',
					'',
					'date'
				)
			],
			[
				'type',
				() => new DropdownFilter(
					'type',
					this.translationService.getTR('commercial-documents.type'),
					this.translationService.getTR('commercial-documents.type'),
					'type',
					'type',
					new FilterApiRequest(GET_DOCUMENTS_V2_URL, { limit: 1500 }),
					undefined,
					undefined,
					undefined,
					false,
					undefined,
					false,
					true
				)
			],
			[
				'subtype',
				() => new DropdownFilter(
					'subtype',
					this.translationService.getTR('commercial-documents.subtype'),
					this.translationService.getTR('commercial-documents.subtype'),
					'subtype',
					'subtype',
					new FilterApiRequest(GET_DOCUMENTS_V2_URL, { limit: 1500 }),
					undefined,
					undefined,
					undefined,
					false,
					undefined,
					false,
					true
				)
			],
			[
				'customerOrderNumber',
				() => new DocumentOrderNumberFilter(
					'customerOrderNumber',
					this.translationService.getTR('commercial-documents.customer-order-number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'customerOrderReference',
					'documentLines',
					'documentLines.customerOrderItemReference'
				)
			],
			[
				'supplierOrderNumber',
				() => new DocumentOrderNumberFilter(
					'supplierOrderNumber',
					this.translationService.getTR('commercial-documents.supplier-order-number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'supplierOrderReference',
					'documentLines',
					'documentLines.supplierOrderReferenceItem'
				)
			],
			[
				'millOrderNumber',
				() => new OrderNumberFilter(
					'millOrderNumber',
					this.translationService.getTR('commercial-documents.mill-order-number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'documentLines.millOrderReference',
					'documentLines.millOrderReferenceItem'
				)
			],
			[
				'soldtoName',
				() => new DocumentSuggestiveListFilterWithId(
					'soldtoName',
					this.translationService.getTR('commercial-documents.customer'),
					this.translationService.getTR('commercial-documents.customer'),
					'soldtoBusinessId',
					'soldtoName',
					'documentLines',
					new FilterApiRequest(GET_DOCUMENTS_V2_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'mill',
				() => new DocumentMillDropdownFilter(
					'mill',
					this.translationService.getTR('commercial-documents.mill'),
					this.translationService.getTR('commercial-documents.mill'),
					'millCode',
					'millDescription',
					'documentLines',
					new FilterApiRequest(`${ GET_DOCUMENTS_URL }:getMillForDocument`, { limit: 1500 })
				)
			],
			[
				'shiptoName',
				() => new DocumentSuggestiveListFilterWithId(
					'shiptoName',
					this.translationService.getTR('commercial-documents.shipto'),
					this.translationService.getTR('commercial-documents.shipto'),
					'shiptoBusinessId',
					'shiptoName',
					'documentLines',
					new FilterApiRequest(GET_DOCUMENTS_V2_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'batchNumber',
				() => new DatacenterFreeTextFilter(
					'batchNumber',
					this.translationService.getTR('commercial-documents.batch-number'),
					this.translationService.getTR('commercial-documents.batch-number'),
					'documentLines.batchNumber'
				)
			],
			[
				'rrpType',
				() => new DatacenterFreeTextFilter(
					'rrpType',
					this.translationService.getTR('doc-xcarb.rrpType'),
					this.translationService.getTR('doc-xcarb.rrpType'),
					'rrpType'
				)
			],
			[
				'rrpDocumentLink',
				() => new DatacenterFreeTextFilter(
					'rrpDocumentLink',
					this.translationService.getTR('doc-xcarb.rrpDownloadLink'),
					this.translationService.getTR('doc-xcarb.rrpDownloadLink'),
					'rrpDocumentLink'
				)
			],
			[
				'amount',
				() => new DatacenterNumericFilter(
					'amount',
					this.translationService.getTR('doc-xcarb.amount'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'amount'
				)
			],
			[
				'endQuarter',
				() => new DatacenterFreeTextFilter(
					'endQuarter',
					this.translationService.getTR('doc-xcarb.endquarter'),
					this.translationService.getTR('doc-xcarb.endquarter'),
					'documentLines.endQuarter'
				)
			],
			[
				'heatNumber',
				() => new DatacenterFreeTextFilter(
					'heatNumber',
					this.translationService.getTR('commercial-documents.heat-number'),
					this.translationService.getTR('commercial-documents.heat-number'),
					'documentLines.heatNumber'
				)
			],
			[
				'invoicedQuantity',
				() => new DatacenterNumericFilter(
					'invoicedQuantity',
					this.translationService.getTR('commercial-documents.invoiced-quantity'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'invoicedQuantity'
				)
			],
			[
				'paymentDueDate',
				() => new DocumentDateFilter(
					'paymentDueDate',
					this.translationService.getTR('commercial-documents.payment-due-date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'paymentDueDate',
					'',
					'date'
				)
			],
			[
				'transportMode',
				() => new DropdownFilter(
					'transportMode',
					this.translationService.getTR('commercial-documents.transport-mode'),
					this.translationService.getTR('commercial-documents.transport-mode'),
					'transportMode',
					'transportMode',
					new FilterApiRequest(GET_DOCUMENTS_V2_URL, { limit: 1500 })
				)
			],
			[
				'transportReference',
				() => new DatacenterFreeTextFilter(
					'transportReference',
					this.translationService.getTR('commercial-documents.transport-reference'),
					this.translationService.getTR('commercial-documents.transport-reference'),
					'documentLines.transportReference'
				)
			],
			[
				'customerArticleReference',
				() => new DatacenterFreeTextFilter(
					'customerArticleReference',
					this.translationService.getTR('commercial-documents.customer-article-reference'),
					this.translationService.getTR('commercial-documents.customer-article-reference'),
					'documentLines.customerArticleReference'
				)
			],
			[
				'steelgrade',
				() => new DatacenterFreeTextFilter(
					'steelgrade',
					this.translationService.getTR('commercial-documents.steel-grade'),
					this.translationService.getTR('commercial-documents.steel-grade'),
					'documentLines.steelGrade'
				)
			],
			[
				'invoiceAmount',
				() => new DatacenterNumericFilter(
					'invoiceAmount',
					this.translationService.getTR('commercial-documents.invoice-amount'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'invoiceAmount'
				)
			],
			[
				'supplierClaimReference',
				() => new DatacenterFreeTextFilter(
					'supplierClaimReference',
					this.translationService.getTR('commercial-documents.supplier-claim-reference'),
					this.translationService.getTR('commercial-documents.supplier-claim-reference'),
					'supplierClaimReference'
				)
			],
			[
				'endUser',
				() => new DocumentSuggestiveListFilterWithId(
					'endUser',
					this.translationService.getTR('commercial-documents.enduser'),
					this.translationService.getTR('commercial-documents.enduser'),
					'enduserBusinessId',
					'enduserName',
					'documentLines.enduserBusinessId',
					new FilterApiRequest(GET_DOCUMENTLINE_V2_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'payer',
				() => new DocumentSuggestiveListFilterWithId(
					'payer',
					this.translationService.getTR('commercial-documents.payer'),
					this.translationService.getTR('commercial-documents.payer'),
					'payerBusinessId',
					'payerName',
					'documentLines',
					new FilterApiRequest(GET_DOCUMENTS_V2_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'payerVatNumber',
				() => new DatacenterFreeTextFilter(
					'payerVatNumber',
					this.translationService.getTR('commercial-documents.payer-vat-number'),
					this.translationService.getTR('commercial-documents.payer-vat-number'),
					'payerVatNumber'
				)
			],
			[
				'dispatchAdviceDate',
				() => new DocumentDateFilter(
					'dispatchAdviceDate',
					this.translationService.getTR('commercial-documents.dispatch-advice-date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'dispatchAdviceDate',
					'documentLines',
					'date'
				)
			],
			[
				'orderedQuantity',
				() => new DatacenterNumericFilter(
					'orderedQuantity',
					this.translationService.getTR('commercial-documents.ordered-quantity'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'documentLines.orderedQuantity'
				)
			],
			[
				'receiptDocumentDate',
				() => new DocumentLastConnectionDateFilter(
					'receiptDocumentDate',
					this.translationService.getTR('commercial-documents.docsWithCreationDateGtLastConnectionDate'),
					'receiptDocumentDate',
					this.user.previousLastConnectionDate
				)
			],
			[
				'receivedBySteeluser',
				() => new DocumentDateFilter(
					'receivedBySteeluser',
					this.translationService.getTR('commercial-documents.received-by-steeluser'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'receiptDocumentDate',
					'',
					'date'
				)
			],
			[
				'proforma',
				() => new DropdownFilter(
					'proforma',
					this.translationService.getTR('commercial-documents.proforma'),
					this.translationService.getTR('commercial-documents.proforma'),
					'proforma',
					'proforma',
					new FilterApiRequest(GET_DOCUMENTS_V2_URL, { limit: 1500 }),
					[],
					undefined,
					undefined,
					false,
					undefined,
					false,
					true
				)
			],
			[
				'globalSearch',
				() => new GlobalSearchFilter(
					'globalSearch',
					this.translationService.getTR('global.search-filter'),
					this.translationService.getTR('global.search-filter'),
					[
						'name',
						'supplierOrderReference',
						'customerOrderReference',
					]
				)
			]
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	DOCUMENT_DATACENTER_DEFAULT_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'customerOrderNumber',
				() => {
					const filter = new DocumentOrderNumberFilter(
						'customerOrderNumber',
						this.translationService.getTR('commercial-documents.customer-order-number'),
						this.translationService.getTR('filter.order-number.reference'),
						this.translationService.getTR('filter.order-number.item'),
						'customerOrderReference',
						'documentLines',
						'documentLines.customerOrderItemReference',
						[]
					);
					filter.addEmptyValue();
					return filter;
				}
			],
			[
				'supplierOrderNumber',
				() => {
					const filter = new DocumentOrderNumberFilter(
						'supplierOrderNumber',
						this.translationService.getTR('commercial-documents.supplier-order-number'),
						this.translationService.getTR('filter.order-number.reference'),
						this.translationService.getTR('filter.order-number.item'),
						'supplierOrderReference',
						'documentLines',
						'documentLines.supplierOrderReferenceItem',
						[]
					);
					filter.addEmptyValue();
					return filter;
				}
			],
			[
				'batchNumber',
				() => new DatacenterFreeTextFilter(
					'batchNumber',
					this.translationService.getTR('commercial-documents.batch-number'),
					this.translationService.getTR('commercial-documents.batch-number'),
					'documentLines.batchNumber',
					['']
				)
			],
			[
				'millOrderNumber',
				() => new OrderNumberFilter(
					'millOrderNumber',
					this.translationService.getTR('commercial-documents.mill-order-number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'documentLines.millOrderReference',
					'documentLines.millOrderReferenceItem',
					[{}]
				)
			],
			[
				'type',
				() => new DropdownFilter(
					'type',
					this.translationService.getTR('commercial-documents.type'),
					this.translationService.getTR('commercial-documents.type'),
					'type',
					'type',
					new FilterApiRequest(GET_DOCUMENTS_V2_URL, { limit: 1500 }),
					[{}]
				)
			],
			[
				'date',
				() => new DatacenterDateFilter(
					'date',
					this.translationService.getTR('commercial-documents.date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'date',
					'date',
					[
						{
							from: '',
							to: ''
						}
					]
				)
			]
		]);
}
